/** Super-admin: reconcile bank-transfer orders and edit the payment details
 *  learners see. When a transfer arrives, find the order by its reference and
 *  "Mark paid" — that unlocks the packs through the same fulfil path a
 *  gateway would eventually use. */
import { useEffect, useState } from "react";
import * as api from "../../api/adminOrders";
import type { PendingOrder } from "../../api/adminOrders";
import type { PaymentInfo } from "../../api/store";
import { Button, Card, Input, Label, Spinner } from "../../components/ui";
import { useT } from "../../i18n";
import { formatMinor } from "../billing/BillingPage";

const PAY_FIELDS: [keyof PaymentInfo, "store.accountName" | "store.accountNumber" | "store.bankName" | "store.ifsc" | "admin.pay.contactEmail" | "admin.pay.note"][] = [
  ["account_name", "store.accountName"],
  ["account_number", "store.accountNumber"],
  ["bank_name", "store.bankName"],
  ["ifsc", "store.ifsc"],
  ["contact_email", "admin.pay.contactEmail"],
  ["note", "admin.pay.note"],
];

function PaymentDetails() {
  const t = useT();
  const [info, setInfo] = useState<PaymentInfo | null>(null);
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.getPaymentInfo().then(setInfo).catch(() => setInfo(null));
  }, []);

  if (!info) return <Spinner />;

  const save = async () => {
    setBusy(true);
    setSaved(false);
    try {
      setInfo(await api.updatePaymentInfo(info));
      setSaved(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card title={t("admin.pay.title")}>
      <p className="mb-4 text-sm text-muted">{t("admin.pay.subtitle")}</p>
      <div className="grid gap-3 sm:grid-cols-2">
        {PAY_FIELDS.map(([field, label]) => (
          <div key={field}>
            <Label htmlFor={field}>{t(label)}</Label>
            <Input
              id={field}
              value={info[field]}
              onChange={(e) => {
                setSaved(false);
                setInfo({ ...info, [field]: e.target.value });
              }}
            />
          </div>
        ))}
      </div>
      <div className="mt-4 flex items-center gap-3">
        <Button onClick={save} loading={busy}>{t("admin.pay.save")}</Button>
        {saved && <span role="status" className="text-sm text-brand">{t("admin.pay.saved")}</span>}
      </div>
    </Card>
  );
}

function PendingOrders() {
  const t = useT();
  const [orders, setOrders] = useState<PendingOrder[] | null>(null);
  const [note, setNote] = useState<string | null>(null);

  const load = () => api.listOrders().then(setOrders).catch(() => setOrders([]));
  useEffect(() => { load(); }, []);

  if (!orders) return <Spinner />;

  const act = async (fn: Promise<unknown>, message: string) => {
    await fn;
    setNote(message);
    load();
  };

  return (
    <Card title={t("admin.orders.title")}>
      <p className="mb-4 text-sm text-muted">{t("admin.orders.subtitle")}</p>
      {note && <p role="status" className="mb-3 text-sm text-brand">{note}</p>}
      {orders.length === 0 ? (
        <p className="text-sm text-muted">{t("admin.orders.none")}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-line text-xs uppercase text-muted">
                <th className="py-2 pr-3">{t("admin.orders.ref")}</th>
                <th className="py-2 pr-3">{t("admin.orders.tenant")}</th>
                <th className="py-2 pr-3">{t("admin.orders.items")}</th>
                <th className="py-2 pr-3">{t("admin.orders.amount")}</th>
                <th className="py-2" />
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => (
                <tr key={o.id} className="border-b border-line">
                  <td className="py-2 pr-3 font-mono font-semibold text-ink">{o.reference}</td>
                  <td className="py-2 pr-3 text-ink">{o.tenant_name}</td>
                  <td className="py-2 pr-3 text-muted">{o.pack_codes.length}</td>
                  <td className="py-2 pr-3 font-semibold text-ink">
                    {formatMinor(o.amount_minor, o.currency)}
                  </td>
                  <td className="py-2">
                    <div className="flex justify-end gap-2">
                      <Button
                        onClick={() => act(api.markOrderPaid(o.id), t("admin.orders.paidDone"))}
                      >
                        {t("admin.orders.markPaid")}
                      </Button>
                      <Button
                        variant="secondary"
                        onClick={() => act(api.cancelOrder(o.id), t("admin.orders.cancelled"))}
                      >
                        {t("admin.orders.cancel")}
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

export function OrdersPage() {
  const t = useT();
  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-3xl font-extrabold text-ink">{t("admin.orders.heading")}</h1>
        <p className="mt-1 text-muted">{t("admin.orders.headingSub")}</p>
      </div>
      <PendingOrders />
      <PaymentDetails />
    </div>
  );
}
